import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Download, MessageCircle, Phone, RotateCcw } from "lucide-react";
import { useLang } from "@/i18n";
import { API, PHONES, waLink, triggerApkDownload } from "@/lib/site";

const EMPTY = {
    name: "",
    phone: "",
    move_from: "",
    move_to: "",
    service: "",
    preferred_date: "",
    details: "",
};

export default function BookingForm({ onDownload }) {
    const { t } = useLang();
    const c = t.form;
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);
    const [success, setSuccess] = useState(null);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const res = await axios.post(`${API}/enquiries`, form);
            if (res.data && res.data.id) {
                setSuccess(form);
                toast.success(c.success.title);
            } else {
                toast.error("Something went wrong. Please call us instead.");
            }
        } catch {
            toast.error("Could not save your enquiry. Please call us instead.");
        } finally {
            setSending(false);
        }
    };

    const waText = (s) =>
        `Hello Azmthullah Packers & Movers! New booking enquiry:\nName: ${s.name}\nPhone: ${s.phone}\nMoving from: ${s.move_from}\nMoving to: ${s.move_to}\nService: ${s.service}\nDate: ${s.preferred_date || "-"}\nDetails: ${s.details || "-"}`;

    const inputCls =
        "h-12 w-full rounded-xl border border-emerald-900/15 bg-white px-4 text-sm text-ink outline-none transition-all placeholder:text-slate-400 focus:border-brand focus:ring-4 focus:ring-brand/15";

    return (
        <section id="book" className="scroll-mt-24 bg-brand-soft/60 py-16 sm:py-24">
            <div className="mx-auto max-w-2xl px-4 sm:px-6">
                {!success ? (
                    <motion.div
                        initial={{ opacity: 0, y: 26 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.15 }}
                        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        className="rounded-3xl border border-emerald-900/10 bg-white p-6 shadow-lg shadow-emerald-900/5 sm:p-8"
                    >
                        <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand">{c.eyebrow}</p>
                        <h2 className="font-display mt-2 text-2xl font-bold tracking-tight text-ink sm:text-3xl">
                            {c.title}
                        </h2>
                        <p className="mt-2 text-sm text-slate-600">{c.sub}</p>

                        <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="booking-enquiry-form">
                            <div>
                                <label className="mb-1.5 block text-xs font-semibold text-slate-600" htmlFor="f-name">{c.f.name}</label>
                                <input id="f-name" required value={form.name} onChange={set("name")} placeholder={c.ph.name} className={inputCls} data-testid="enquiry-input-name" />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-semibold text-slate-600" htmlFor="f-phone">{c.f.phone}</label>
                                <input id="f-phone" required type="tel" inputMode="tel" value={form.phone} onChange={set("phone")} placeholder={c.ph.phone} className={inputCls} data-testid="enquiry-input-phone" />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-semibold text-slate-600" htmlFor="f-from">{c.f.from}</label>
                                <input id="f-from" required value={form.move_from} onChange={set("move_from")} placeholder={c.ph.from} className={inputCls} data-testid="enquiry-input-from" />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-semibold text-slate-600" htmlFor="f-to">{c.f.to}</label>
                                <input id="f-to" required value={form.move_to} onChange={set("move_to")} placeholder={c.ph.to} className={inputCls} data-testid="enquiry-input-to" />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-semibold text-slate-600" htmlFor="f-service">{c.f.service}</label>
                                <select id="f-service" required value={form.service} onChange={set("service")} className={`${inputCls} appearance-none`} data-testid="enquiry-input-service">
                                    <option value="" disabled>{c.pick}</option>
                                    {c.services.map((s) => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-semibold text-slate-600" htmlFor="f-date">{c.f.date}</label>
                                <input id="f-date" type="date" value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} data-testid="enquiry-input-date" />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="mb-1.5 block text-xs font-semibold text-slate-600" htmlFor="f-details">{c.f.details}</label>
                                <textarea id="f-details" rows={3} value={form.details} onChange={set("details")} placeholder={c.ph.details} className="w-full rounded-xl border border-emerald-900/15 bg-white px-4 py-3 text-sm text-ink outline-none transition-all placeholder:text-slate-400 focus:border-brand focus:ring-4 focus:ring-brand/15" data-testid="enquiry-input-details" />
                            </div>
                            <button
                                type="submit"
                                disabled={sending}
                                data-testid="enquiry-form-submit-button"
                                className="h-13 min-h-12 w-full rounded-full bg-brand text-lg font-bold text-white shadow-lg shadow-emerald-600/25 transition-all hover:-translate-y-0.5 hover:bg-brand-dark disabled:opacity-60 sm:col-span-2"
                            >
                                {sending ? c.sending : c.submit}
                            </button>
                        </form>
                    </motion.div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.97 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className="rounded-3xl border border-emerald-900/10 bg-white p-6 text-center shadow-lg shadow-emerald-900/5 sm:p-10"
                        data-testid="enquiry-success-confirmation-card"
                    >
                        <CheckCircle2 className="mx-auto h-14 w-14 text-brand" />
                        <h2 className="font-display mt-4 text-2xl font-bold text-ink sm:text-3xl">{c.success.title}</h2>
                        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-600">{c.success.sub}</p>

                        <button
                            type="button"
                            onClick={() => { triggerApkDownload(); onDownload(); }}
                            data-testid="success-apk-download-button"
                            className="mt-6 flex h-14 w-full items-center justify-center gap-2.5 rounded-full bg-brand text-lg font-bold text-white shadow-lg shadow-emerald-600/25 transition-all hover:-translate-y-0.5 hover:bg-brand-dark"
                        >
                            <Download className="h-5 w-5" />
                            {c.success.apk}
                        </button>
                        <p className="mt-2 text-xs text-slate-500">{c.success.apkHint}</p>

                        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <a
                                href={waLink(PHONES[0].wa, waText(success))}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="success-whatsapp-send-button"
                                className="flex h-12 items-center justify-center gap-2 rounded-full bg-[#25D366] font-semibold text-white transition-all hover:brightness-95"
                            >
                                <MessageCircle className="h-4 w-4" />
                                {c.success.wa}
                            </a>
                            <a
                                href={`tel:${PHONES[0].tel}`}
                                data-testid="success-call-button"
                                className="flex h-12 items-center justify-center gap-2 rounded-full border-2 border-brand font-semibold text-brand transition-colors hover:bg-brand-soft"
                            >
                                <Phone className="h-4 w-4" />
                                {c.success.call}
                            </a>
                        </div>

                        <button
                            type="button"
                            onClick={() => { setSuccess(null); setForm(EMPTY); }}
                            data-testid="success-new-enquiry-button"
                            className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-brand"
                        >
                            <RotateCcw className="h-3.5 w-3.5" />
                            {c.success.again}
                        </button>
                    </motion.div>
                )}
            </div>
        </section>
    );
}
